import { useState, useEffect } from 'react';
import WidgetCard from './WidgetCard';
import '../../css/PTZWidget.css';
import { useScreenSize } from '../../services/General/ScreenResolutionProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faArrowUp, faArrowDown, faSearchMinus, faSearchPlus, faVideoCamera, faHome, faCheck, faCamera, faReply, faCompass, faUpDownLeftRight } from '@fortawesome/free-solid-svg-icons';
import PTZService from '../../services/Video/PTZService';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useOperator } from '../../utilities/http/routing/OperatorContext';
import TimerInput from '../Timers/TimerInput';
import { useFetch } from '../../services/Requests/useFetch';
import { useRobotDetails } from '../../utilities/RobotDetailsContext';

const postOptions: RequestInit = {
    method: "POST"
}

interface PTZRecord {
    duration: number
}

interface PTZState {
    pan: number
    tilt: number
    zoom: number
}


export const PTZWidget = () => {
    const {robotDetails} = useRobotDetails();
    const { width } = useScreenSize();
    const [ptzSpeed, setPtzSpeed] = useState(0.2);
    const [presetNumber, setPresetNumber] = useState('1');
    const [illegalpresetNumber, setillegalPresetNumber] = useState(1);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [isIllegal, setIsIllegal] = useState<boolean>(false)
    const [timeInput, setTimeInput] = useState<number>(0);
    const [countdown, setCountdown] = useState<number>(0);
    const [quickTurnActive, changeQuickTurn] = useState<boolean>(true)
    const [precisionMode, setPrecisionMode] = useState<boolean>(true)
    const [snapshotTaken, setSnapshotTaken] = useState(false);
    const [isPTZRecording, setIsPTZRecording] = useState(false); // Added state for PTZ recording
    const [ptzState, setPTZState] = useState<PTZState>({pan:0,tilt:0,zoom:0})

    const PAN_MIN = 0;
    const PAN_MAX = 360;

    const TILT_MIN = -30;
    const TILT_MAX = 30;

    const ZOOM_MIN = 1;
    const ZOOM_MAX = 40;

    const startPTZRecord = useFetch<PTZRecord>('/api/missions/ptz_recording_video', postOptions);

    const illegalPresets = [37, 44, 45, 46, 47,48, 49, 50, 51, 81, 82, 83, 84, 85, 86, 89, 90, 91, 92, 93, 
        94, 95, 96, 97, 98, 99, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 
        113, 114, 115, 116, 117, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 
        129, 130, 131, 132, 133, 135, 136, 138, 139, 141, 142, 143, 144, 152, 153, 
        157, 159, 160, 161, 162, 163, 164, 168, 169, 170, 171, 172, 173, 174, 181, 
        182, 183, 184, 186, 187, 188, 190, 191, 192, 193, 194]
    const { isOperator } = useOperator();
    let position = 'widget-top-right';

    if (width <= 1024) {
        position = 'widget-top-right';
    }

    const handlePTZRefresh = async() => {
        try{
            const updatedPTZData = await PTZService.getAngle(robotDetails.ptzServer)
            setPTZState(updatedPTZData)
        } catch (error) {
            console.error('Failed to get PTZ angle', error);
        }
    }

    useEffect(() => {
        const handleMouseUp = () => {
            if (isMouseDown) {
                setIsMouseDown(false);
                handlePTZStop();
            }
        };

        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isMouseDown]);

    useEffect(()=> {
        handlePTZRefresh()
    }, []);

    const handlePanDial = (
        event: React.PointerEvent<HTMLDivElement>
    ) => {
        const rect = event.currentTarget.getBoundingClientRect();

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const x = event.clientX - centerX;
        const y = event.clientY - centerY;

        // atan2 gives angle from -180 to +180.
        // Add 90° so 0° is at the top.
        let angle = Math.atan2(x, -y) * (180 / Math.PI);

        if (angle < 0) {
            angle += 360;
        }

        setPTZState(prev => ({
            ...prev,
            pan: Math.round(angle),
        }));
    };

    const handlePanPointerDown = (
        event: React.PointerEvent<HTMLDivElement>
    ) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        handlePanDial(event);
    };

    const handlePanPointerMove = (
        event: React.PointerEvent<HTMLDivElement>
    ) => {
        if (event.buttons === 1) {
            handlePanDial(event);
        }
    };


    const handleGoToAngle = async(target: PTZState)=> {
         try {
            let pan_override = target.pan
            if (target.pan === 180 ){
                pan_override = 179
            } 
            await PTZService.gotoAngle(pan_override,target.tilt,target.zoom,robotDetails.ptzServer, ptzSpeed);
            console.log(`Moving PTZ: ${target} at speed ${ptzSpeed}`);
        } catch (error) {
            console.error('Failed to move PTZ', error);
        }
    }

    

    const handlePTZMove = async (direction: string) => {
        try {
            await PTZService.movePTZ(direction,robotDetails.ptzServer, ptzSpeed);
            console.log(`Moving PTZ: ${direction} at speed ${ptzSpeed}`);
        } catch (error) {
            console.error('Failed to move PTZ', error);
        }
    };

    const handleQuickTurnSwap = () => {
        changeQuickTurn(!quickTurnActive)
    }

    const handlePrecisionModeswap = () => {
        setPrecisionMode(!precisionMode)
    }

    const handlePTZStop = async () => {
        try {
            await PTZService.movePTZ('stop',robotDetails.ptzServer);
            console.log('PTZ Stopped');
        } catch (error) {
            console.error('Failed to stop PTZ', error);
        }
    };

    const handlePTZHome = async () => {
        try {
            await PTZService.gotoHomePTZ(robotDetails.ptzServer);
            console.log('PTZ Homed');
        } catch (error) {
            console.error('Failed to Home PTZ', error);
        }
    };

    const handlegotoPreset = async () => {
        try {
            await PTZService.gotoPreset(presetNumber,robotDetails.ptzServer);
            console.log('PTZ went to Preset ' + presetNumber);
        } catch (error) {
            console.error('Failed to go to Preset', error);
        }
    };


    const handlesetPreset = async () => {
        try {
            await PTZService.setPreset(presetNumber,robotDetails.ptzServer);
            console.log('PTZ set to Preset ' + presetNumber);
        } catch (error) {
            console.error('Failed to set Preset', error);
        }
    };

    // const handleWipe = async () => {
    //     try {
    //         await PTZService.wipeLens(robotDetails.ptzServer);
    //         console.log('PTZ set to Preset 51 and wiping');
    //     } catch (error) {
    //         console.error('Failed to set Preset', error);
    //     }
    //     return
    // };

    const handleQuickTurn = async(dir: number)=>{
        try {
            await PTZService.gotoPreset(dir,robotDetails.ptzServer);
            console.log('PTZ went to Preset ' + dir);
        } catch (error) {
            console.error('Failed to go to Preset', error);
        }
        
    }

    const handlePTZSnapshot = async () => {
        console.log('Taking PTZ Snapshot');
        await PTZService.snapshotPTZ(robotDetails.ptzServer);
        console.log('Snapshot taken successfully');
    };

    const handleSnapshot = async () => {
        try {
            await handlePTZSnapshot();

            setSnapshotTaken(true);

            setTimeout(() => {
                setSnapshotTaken(false);
            }, 1500);
        } catch (error) {
            console.error("Failed to take snapshot:", error);
        }
    };

    const handleSpeedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPtzSpeed(parseFloat(event.target.value));
    };
    
    const formatTime = (seconds: number): string => {
        const hh = Math.floor(seconds / 3600);
        const mm = Math.floor((seconds % 3600) / 60);
        const ss = seconds % 60;
        return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    };
    
    const startCountdown = () => {
        const totalSeconds = timeInput;
        startPTZRecord.post({duration: totalSeconds})
        if (totalSeconds > 0) {
            setCountdown(totalSeconds);
            setIsPTZRecording(true);
            const interval = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setIsPTZRecording(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
    };

    const handlePresetNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.max(1, Math.min(255, parseInt(event.target.value)));
        if (!illegalPresets.includes(value)){
            setIsIllegal(false)
            setPresetNumber(value.toString());

        }else{
            setIsIllegal(true)
            setillegalPresetNumber(value)
            console.log("Illegal Value")
        }
    };

    const handleDirectionButtonDown = (direction: string) => {
        setIsMouseDown(true);
        handlePTZMove(direction);
    };

    return (
        <WidgetCard position={position}>
            <WidgetCard.Header>
                PTZ Control
            </WidgetCard.Header>

            <WidgetCard.Body>
                {/* ==================== CAMERA CONTROL ==================== */}
                <div className="ptz-section">

                    {precisionMode ? <div className="ptz-camera-panel">
                        <div className="ptz-pad-box">
                            <button
                                type="button"
                                className="ptz-goto-home"
                                onMouseDown={handlePTZHome}
                                aria-label="Home camera"
                            >
                                <FontAwesomeIcon icon={faHome} />
                            </button>

                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className="left"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('left')
                                }
                            />

                            <FontAwesomeIcon
                                icon={faArrowRight}
                                className="right"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('right')
                                }
                            />

                            <FontAwesomeIcon
                                icon={faArrowUp}
                                className="up"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('up')
                                }
                            />

                            <FontAwesomeIcon
                                icon={faArrowDown}
                                className="down"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('down')
                                }
                            />
                        </div>

                        {/* Zoom controls beside the camera pad */}
                        <div className="ptz-zoom-control">
                            <button
                                type="button"
                                className="ptz-zoom"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('zoom-in')
                                }
                                aria-label="Zoom in"
                            >
                                <FontAwesomeIcon icon={faSearchPlus} />
                            </button>

                            <button
                                type="button"
                                className="ptz-zoom"
                                onMouseDown={() =>
                                    handleDirectionButtonDown('zoom-out')
                                }
                                aria-label="Zoom out"
                            >
                                <FontAwesomeIcon icon={faSearchMinus} />
                            </button>
                        </div>
                    </div> :
                    (
                        <div className="precision-ptz-panel">

                            <div className="precision-ptz-controls">

                                {/* ================= PAN DIAL ================= */}
                                <div className="precision-pan-section">
                                    <div className="precision-control-title">
                                        Pan
                                    </div>

                                    <div
                                        className="pan-dial"
                                        onPointerDown={handlePanPointerDown}
                                        onPointerMove={handlePanPointerMove}
                                    >
                                        <span className="pan-marker pan-north">
                                            0°
                                        </span>

                                        <span className="pan-marker pan-ne">
                                            45°
                                        </span>

                                        <span className="pan-marker pan-east">
                                            90°
                                        </span>

                                        <span className="pan-marker pan-se">
                                            135°
                                        </span>

                                        <span className="pan-marker pan-south">
                                            180°
                                        </span>

                                        <span className="pan-marker pan-sw">
                                            225°
                                        </span>

                                        <span className="pan-marker pan-west">
                                            270°
                                        </span>

                                        <span className="pan-marker pan-nw">
                                            315°
                                        </span>

                                        <div
                                            className="pan-indicator"
                                            style={{
                                                transform: `rotate(${ptzState.pan}deg)`
                                            }}
                                        >
                                            <div className="pan-arrow">
                                                ▲
                                            </div>
                                        </div>

                                        <div className="pan-value">
                                            {Math.round(ptzState.pan)}°
                                        </div>
                                    </div>
                                </div>


                                {/* ================= TILT / ZOOM ================= */}
                                <div className="precision-sliders">

                                    {/* Tilt */}
                                    <div className="precision-slider">
                                        <div className="precision-control-header">
                                            <span>Tilt</span>

                                            <span className="precision-value">
                                                {Math.round(ptzState.tilt)}°
                                            </span>
                                        </div>

                                        <div className="precision-slider-row">
                                            <span>{TILT_MIN}°</span>

                                            <input
                                                type="range"
                                                min={TILT_MIN}
                                                max={TILT_MAX}
                                                step="1"
                                                value={ptzState.tilt}
                                                onChange={(event) =>
                                                    setPTZState(prev => ({
                                                        ...prev,
                                                        tilt: Number(event.target.value),
                                                    }))
                                                }
                                            />

                                            <span>{TILT_MAX}°</span>
                                        </div>
                                    </div>


                                    {/* Zoom */}
                                    <div className="precision-slider">
                                        <div className="precision-control-header">
                                            <span>Zoom</span>

                                            <span className="precision-value">
                                                {Math.round(ptzState.zoom)}
                                            </span>
                                        </div>

                                        <div className="precision-slider-row">
                                            <span>{ZOOM_MIN}</span>

                                            <input
                                                type="range"
                                                min={ZOOM_MIN}
                                                max={ZOOM_MAX}
                                                step="1"
                                                value={ptzState.zoom}
                                                onChange={(event) =>
                                                    setPTZState(prev => ({
                                                        ...prev,
                                                        zoom: Number(event.target.value),
                                                    }))
                                                }
                                            />

                                            <span>{ZOOM_MAX}</span>
                                        </div>
                                    </div>

                                </div>
                            </div>


                            {/* ================= GO TO ANGLE ================= */}
                            <button
                                type="button"
                                className="btn btn-primary precision-goto-button"
                                onClick={() => {
                                    const target = {
                                        pan: ptzState.pan,
                                        tilt: ptzState.tilt,
                                        zoom: ptzState.zoom,
                                    };

                                    handleGoToAngle(target);
                                }}
                            >
                                Turn PTZ
                            </button>

                        </div>
                    )}

                    {/* Compact speed selector */}
                    <div className="ptz-speed-control">
                        <span className="ptz-speed-label">
                            Speed
                        </span>

                        <div
                            className="ptz-speed-buttons"
                            role="group"
                            aria-label="PTZ speed"
                        >
                            <label className={ptzSpeed === 0.1 ? "active" : ""}>
                                <input
                                    type="radio"
                                    name="ptz-speed"
                                    value="0.1"
                                    onChange={handleSpeedChange}
                                />
                                |
                            </label>

                            <label className={ptzSpeed === 0.2 ? "active" : ""}>
                                <input
                                    type="radio"
                                    name="ptz-speed"
                                    value="0.2"
                                    defaultChecked
                                    onChange={handleSpeedChange}
                                />
                                ||
                            </label>

                            <label className={ptzSpeed === 0.35 ? "active" : ""}>
                                <input
                                    type="radio"
                                    name="ptz-speed"
                                    value="0.35"
                                    onChange={handleSpeedChange}
                                />
                                |||
                            </label>
                        </div>
                        {isOperator &&<button
                                type="reset"
                                className='btn'
                                onClick={handlePrecisionModeswap}
                                aria-label="changeQuickTurn"
                            >
                                <FontAwesomeIcon icon={precisionMode ? faCompass : faUpDownLeftRight} />
                        </button>}
                    </div>
                </div>

                {/* ==================== QUICK TURN ==================== */}
                {quickTurnActive ? <div className="ptz-section quick-turn-section">
                    <div className="ptz-section-title">
                        Quick Turn
                        {isOperator &&<button
                                type="reset"
                                className='btn'
                                onClick={handleQuickTurnSwap}
                                aria-label="changeQuickTurn"
                            >
                                <FontAwesomeIcon icon={faReply} />
                        </button>}
                    </div>

                   <div className="quick-turn-grid">
                    <button
                        type="button"
                        className="btn btn-default quick-turn-button"
                        onClick={() => handleQuickTurn(1)}
                        aria-label="Turn forward"
                    >
                        <span className="quick-turn-arrow">↑</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn-default quick-turn-button"
                        onClick={() => handleQuickTurn(2)}
                        aria-label="Turn left"
                    >
                        <span className="quick-turn-arrow">←</span>
                    </button>

                    <div
                        className="robot-centre"
                        aria-label="Robot orientation"
                    >
                        <span className="robot-icon">▲</span>
                    </div>

                    <button
                        type="button"
                        className="btn btn-default quick-turn-button"
                        onClick={() => handleQuickTurn(3)}
                        aria-label="Turn right"
                    >
                        <span className="quick-turn-arrow">→</span>
                    </button>

                    <button
                        type="button"
                        className="btn btn-default quick-turn-button"
                        onClick={() => handleQuickTurn(4)}
                        aria-label="Turn back"
                    >
                        <span className="quick-turn-arrow">↓</span>
                    </button>
                </div>
                </div> :
                
                    <div className="ptz-section">
                            {/* ==================== PRESETS ==================== */}
                        <div className="ptz-section-title">
                            Presets
                            {isOperator &&<button
                                type="reset"
                                className='btn'
                                onClick={handleQuickTurnSwap}
                                aria-label="changeQuickTurn"
                            >
                                <FontAwesomeIcon icon={faReply} />
                        </button>}
                        </div>

                        <OverlayTrigger
                            placement="left-start"
                            show={isIllegal}
                            overlay={
                                <Tooltip id="tooltip-preset">
                                    Preset {illegalpresetNumber} is not allowed
                                </Tooltip>
                            }
                        >
                            <input
                                type="number"
                                className="form-control"
                                value={presetNumber}
                                onChange={handlePresetNumberChange}
                                placeholder="1"
                                min="1"
                                max="255"
                            />
                        </OverlayTrigger>

                        <button
                            type="button"
                            className="form-control btn btn-default"
                            onClick={handlegotoPreset}
                        >
                            Go to Preset
                        </button>

                        <button
                            type="button"
                            className="form-control btn btn-default"
                            onClick={handlesetPreset}
                        >
                            Set Preset
                        </button>
                    </div>
                
                 }
                {/* ==================== CAMERA CAPTURE ==================== */}
                <div className="ptz-section">
                    <div className="ptz-section-title">
                        Camera Capture
                    </div>

                    <div className="camera-capture-controls">
                        {/* Snapshot */}
                        <button
                            type="button"
                            className={`btn ${
                                snapshotTaken
                                    ? "btn-success"
                                    : "btn-default"
                            } snapshot-button`}
                            onClick={handleSnapshot}
                        >
                            

                            {/* <span>
                                {snapshotTaken
                                    ? "Photo Taken  "
                                    : "Snapshot  "}
                            </span> */}
                            <FontAwesomeIcon
                                icon={snapshotTaken ? faCheck : faCamera}
                            />
                        </button>

                        {/* Recording */}
                        <div className="recording-controls">
                            {isPTZRecording ? (
                                <span className="form-control text-center">
                                    {formatTime(countdown)}
                                </span>
                            ) : (
                                <TimerInput onChange={setTimeInput} />
                            )}

                            <OverlayTrigger
                                placement="left-start"
                                overlay={
                                    <Tooltip id="tooltip-record">
                                        {isPTZRecording
                                            ? "Recording"
                                            : "Start Recording PTZ Video"}
                                    </Tooltip>
                                }
                            >
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={startCountdown}
                                    disabled={isPTZRecording}
                                    aria-label="Start recording"
                                >
                                    <FontAwesomeIcon icon={faVideoCamera} />
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>
                </div>


                
            </WidgetCard.Body>
        </WidgetCard>
    );
};

export default PTZWidget;
